/* eslint-disable @typescript-eslint/no-explicit-any */

import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flag, Info, Reply, Send } from "lucide-react";
import { toast } from "sonner";

import FullScreenImage from "@/components/FullScreenImage";
import KindredSharedContent from "@/components/KindredSharedContent";
import ReactionBar from "@/components/ReactionBar";
import KindredShareSheet from "@/components/KindredShareSheet";
import MentionComposerField from "@/components/MentionComposerField";
import MessageActions from "@/components/MessageActions";
import PhotoUploadButton from "@/components/PhotoUploadButton";
import ReplyQuote from "@/components/ReplyQuote";
import ReportSheet from "@/components/ReportSheet";
import { MemberPageScaffold } from "@/components/MemberScaffold";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useLongPressAction } from "@/hooks/useLongPressAction";
import { supabase } from "@/integrations/supabase/client";
import {
  buildKindredWebUrl,
  composeKindredContent,
  getKindredSharePreview,
  parseKindredShareContent,
} from "@/lib/kindredShare";
import { dispatchMentionNotifications, searchChatRoomMentionSuggestions } from "@/lib/mentions";
import { trackKindredProductEvent } from "@/lib/productAnalytics";
import { resolveSignedStorageUrls, resolveStoredObjectUrl } from "@/lib/storageObjectUrls";
import {
  canEditOwnedContentOnly,
  canRemoveKinfeedOrChannelContent,
  isAdminModeratingOthersContent,
} from "../../shared/system";
import type { MentionDraft } from "../../shared/community";
import { isCampingBreaksRoom } from "../../shared/community/campaigns";
import { getFirstUnreadMessageId, groupScopedReactions } from "../../shared/community";
import {
  AppButton,
  AppButtonRow,
  AppBottomSheetSurface,
  AppCard,
  AppCardBody,
  AppEmptyState,
  AppErrorState,
  AppFieldGroup,
  AppInput,
  AppIconButton,
  AppLoadingState,
  AppSectionHeader,
  AppText,
  AppTextArea,
} from "../../ui-foundation";

const ChatRoomView = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const longPressAction = useLongPressAction();
  const [room, setRoom] = useState<any>(null);
  const [messages, setMessages] = useState<any[]>([]);
  const [reactions, setReactions] = useState<any[]>([]);
  const [isMember, setIsMember] = useState(false);
  const [memberCount, setMemberCount] = useState(0);
  const [newMsg, setNewMsg] = useState("");
  const [newMsgMentions, setNewMsgMentions] = useState<MentionDraft[]>([]);
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editText, setEditText] = useState("");
  const [fullScreenImage, setFullScreenImage] = useState<string | null>(null);
  const [resolvedPhotoUrls, setResolvedPhotoUrls] = useState<Map<string, string>>(new Map());
  const [replyTo, setReplyTo] = useState<any>(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportMsgId, setReportMsgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [editingRoom, setEditingRoom] = useState(false);
  const [roomNameDraft, setRoomNameDraft] = useState("");
  const [roomDescriptionDraft, setRoomDescriptionDraft] = useState("");
  const [savingRoom, setSavingRoom] = useState(false);
  const [deletingRoom, setDeletingRoom] = useState(false);
  const [roomActionsOpen, setRoomActionsOpen] = useState(false);
  const [messageActionsId, setMessageActionsId] = useState<string | null>(null);
  const [firstUnreadMessageId, setFirstUnreadMessageId] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messageRefs = useRef(new Map<string, HTMLDivElement>());
  const initialPositionResolvedRef = useRef(false);
  const initialPositionAppliedRef = useRef(false);
  const shouldFollowMessagesRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!roomId || !user) return;

    const [{ data: nextRoom }, { data: members }] = await Promise.all([
      supabase.from("chat_rooms" as any).select("*").eq("id", roomId).single(),
      supabase.from("chat_room_members" as any).select("user_id, joined_at, last_read_at").eq("room_id", roomId),
    ]);

    if (nextRoom) {
      setRoom(nextRoom);
    } else {
      setRoom(null);
      setMessages([]);
      setReactions([]);
      setMemberCount(0);
      setIsMember(false);
      setLoading(false);
      return;
    }

    const membersList = (members as any[]) || [];
    const nextIsMember = membersList.some((member: any) => member.user_id === user.id);
    const ownMembership = membersList.find((member: any) => member.user_id === user.id) ?? null;
    setMemberCount(membersList.length);
    setIsMember(nextIsMember);

    if (!nextIsMember) {
      setMessages([]);
      setReactions([]);
      setLoading(false);
      return;
    }

    const { data: nextMessages } = await supabase
      .from("chat_room_messages" as any)
      .select("*, profiles(display_name, avatar_url)")
      .eq("room_id", roomId)
      .order("created_at", { ascending: true });

    const visibleMessages = ((nextMessages as any[]) || []).filter((message: any) => !message.is_deleted);
    if (!initialPositionResolvedRef.current) {
      setFirstUnreadMessageId(
        getFirstUnreadMessageId(
          visibleMessages,
          user.id,
          ownMembership?.joined_at,
          ownMembership?.last_read_at,
        ),
      );
      initialPositionResolvedRef.current = true;
    }
    setMessages(visibleMessages);
    if (visibleMessages.length) {
      const { data: nextReactions } = await supabase
        .from("chat_room_message_reactions" as any)
        .select("*")
        .in("message_id", visibleMessages.map((message: any) => message.id));
      setReactions((nextReactions as any[]) || []);
    } else {
      setReactions([]);
    }
    if (ownMembership) {
      const latestActivityAt = Math.max(
        ...visibleMessages.map((message: any) => new Date(message.created_at).getTime()),
        0,
      );
      const readCutoff = Math.max(
        ownMembership.last_read_at ? new Date(ownMembership.last_read_at).getTime() : 0,
        ownMembership.joined_at ? new Date(ownMembership.joined_at).getTime() : 0,
      );

      if (latestActivityAt > readCutoff) {
        await supabase
          .from("chat_room_members" as any)
          .update({ last_read_at: new Date().toISOString() } as any)
          .eq("room_id", roomId)
          .eq("user_id", user.id);
      }
    }
    setLoading(false);
  }, [roomId, user]);

  useEffect(() => {
    initialPositionResolvedRef.current = false;
    initialPositionAppliedRef.current = false;
    shouldFollowMessagesRef.current = true;
    messageRefs.current.clear();
    setFirstUnreadMessageId(null);
  }, [roomId]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (!roomId) return;

    const channel = supabase
      .channel(`chat-room-${roomId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_room_messages", filter: `room_id=eq.${roomId}` },
        () => void fetchData(),
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_room_message_reactions" },
        () => void fetchData(),
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [fetchData, roomId]);

  useEffect(() => {
    if (!initialPositionResolvedRef.current || initialPositionAppliedRef.current) {
      return;
    }

    const frame = window.requestAnimationFrame(() => {
      const target = firstUnreadMessageId
        ? messageRefs.current.get(firstUnreadMessageId)
        : bottomRef.current;
      target?.scrollIntoView({
        behavior: "auto",
        block: firstUnreadMessageId ? "center" : "end",
      });
      initialPositionAppliedRef.current = true;
      shouldFollowMessagesRef.current = true;
    });

    return () => window.cancelAnimationFrame(frame);
  }, [firstUnreadMessageId, messages]);

  useEffect(() => {
    const target = bottomRef.current;
    if (!target || typeof IntersectionObserver === "undefined") {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (initialPositionAppliedRef.current) {
          shouldFollowMessagesRef.current = entry.isIntersecting;
        }
      },
      { rootMargin: "0px 0px 120px 0px" },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [messages.length]);

  useEffect(() => {
    if (!initialPositionAppliedRef.current || !shouldFollowMessagesRef.current) {
      return;
    }

    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  useEffect(() => {
    if (!room) return;

    setRoomNameDraft(room.name ?? "");
    setRoomDescriptionDraft(room.description ?? "");
  }, [room]);

  useEffect(() => {
    let active = true;
    const photoValues = messages.map((message) => message.photo_url).filter(Boolean);

    void resolveSignedStorageUrls("channel-photos", photoValues).then((nextMap) => {
      if (active) {
        setResolvedPhotoUrls(nextMap);
      }
    });

    return () => {
      active = false;
    };
  }, [messages]);

  const joinRoom = async () => {
    if (!user || !roomId) return;
    const { error } = await supabase
      .from("chat_room_members" as any)
      .insert({ room_id: roomId, user_id: user.id } as any);
    if (error) {
      toast.error(error.message || "Couldn't join room");
      return;
    }
    await trackKindredProductEvent({
      userId: user.id,
      eventName: "chat_room_joined",
      screenName: "chat_room",
      entityType: "chat_room",
      entityId: roomId,
      metadata: {
        campaign: isCampingBreaksRoom(roomId) ? "camping_breaks" : null,
      },
    });
    toast.success("Joined room");
    await fetchData();
    setRoomActionsOpen(false);
  };

  const leaveRoom = async () => {
    if (!user || !roomId) return;
    await supabase.from("chat_room_members" as any).delete().eq("room_id", roomId).eq("user_id", user.id);
    toast.success("Left room");
    setRoomActionsOpen(false);
    navigate("/communities?section=rooms");
  };

  const canManageRoom = Boolean(user?.id && room && (room.created_by === user.id || role === "admin"));

  const saveRoom = async () => {
    if (!roomId || !roomNameDraft.trim() || savingRoom) return;

    setSavingRoom(true);
    const { error } = await supabase
      .from("chat_rooms" as any)
      .update({
        name: roomNameDraft.trim(),
        description: roomDescriptionDraft.trim() || null,
      } as any)
      .eq("id", roomId);
    setSavingRoom(false);

    if (error) {
      toast.error(error.message || "Couldn't update room");
      return;
    }

    toast.success("Room updated");
    setEditingRoom(false);
    setRoomActionsOpen(false);
    await fetchData();
  };

  const deleteRoom = async () => {
    if (!roomId || deletingRoom) return;

    if (!window.confirm(`Delete ${room?.name ?? "this room"}? This removes the room and its messages for everyone.`)) {
      return;
    }

    setDeletingRoom(true);
    const { error } = await supabase
      .from("chat_rooms" as any)
      .delete()
      .eq("id", roomId);
    setDeletingRoom(false);

    if (error) {
      toast.error(error.message || "Couldn't delete room");
      return;
    }

    toast.success("Room deleted");
    setRoomActionsOpen(false);
    navigate("/communities?section=rooms");
  };

  const send = async () => {
    if ((!newMsg.trim() && !photoUrl) || !user || !roomId || sending) return;
    setSending(true);
    shouldFollowMessagesRef.current = true;
    const { error } = await supabase.from("chat_room_messages" as any).insert({
      room_id: roomId,
      user_id: user.id,
      content: newMsg.trim() || "📷",
      photo_url: photoUrl,
      reply_to_id: replyTo?.id || null,
    } as any);
    if (error) {
      toast.error("Couldn't send message");
      setSending(false);
      return;
    }
    await dispatchMentionNotifications({
      actorId: user.id,
      actorDisplayName:
        user.user_metadata?.display_name ?? user.email?.split("@")[0] ?? "Someone",
      content: newMsg.trim(),
      mentions: newMsgMentions,
      bodyContext: `in ${room?.name ?? "a room"}`,
      entityType: "room",
      entityId: roomId,
      url: `/community/room/${roomId}`,
    });
    setNewMsg("");
    setNewMsgMentions([]);
    setPhotoUrl(null);
    setReplyTo(null);
    setSending(false);
  };

  const deleteMsg = async (messageId: string, hiddenByAdmin = false) => {
    await supabase.from("chat_room_messages" as any).update({ is_deleted: true } as any).eq("id", messageId);
    await fetchData();
    if (hiddenByAdmin) {
      toast.success("Message hidden");
    }
  };

  const saveEdit = async (messageId: string) => {
    if (!editText.trim()) return;
    const existingMessage = messages.find((message) => message.id === messageId);
    const parsed = parseKindredShareContent(existingMessage?.content);
    await supabase
      .from("chat_room_messages" as any)
      .update({
        content: composeKindredContent(editText, parsed.payload),
        edited_at: new Date().toISOString(),
      } as any)
      .eq("id", messageId);
    setEditingId(null);
    await fetchData();
  };

  const toggleReaction = async (messageId: string, emoji: string) => {
    if (!user) return;

    const existing = reactions.find(
      (reaction: any) => reaction.message_id === messageId && reaction.user_id === user.id && reaction.emoji === emoji,
    );

    if (existing) {
      await supabase.from("chat_room_message_reactions" as any).delete().eq("id", existing.id);
    } else {
      await supabase.from("chat_room_message_reactions" as any).insert({ message_id: messageId, user_id: user.id, emoji });
    }
  };

  const formatTime = (timestamp: string) =>
    new Date(timestamp).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });

  const getReplyMessage = (replyToId: string) => messages.find((message) => message.id === replyToId);

  if (loading) {
    return (
      <MemberPageScaffold title="Chat room" subtitle="Loading the room and messages." fallbackPath="/communities?section=rooms">
        <AppLoadingState title="Loading room" body="Pulling in the latest messages." />
      </MemberPageScaffold>
    );
  }

  if (!room) {
    return (
      <MemberPageScaffold title="Chat room" subtitle="This room is no longer available." fallbackPath="/communities?section=rooms">
        <AppErrorState
          title="Room not found"
          body="It may have been deleted or the link may be out of date."
          action={
            <AppButton type="button" tone="outline" onPress={() => navigate("/communities?section=rooms")}>
              Back to Community
            </AppButton>
          }
        />
      </MemberPageScaffold>
    );
  }

  const sharePayload = {
    version: 1 as const,
    type: "room" as const,
    targetId: room.id,
    title: room.name,
    subtitle: room.description || `${memberCount} members`,
    imageUrl: null,
    route: `/community/room/${room.id}`,
    externalUrl: buildKindredWebUrl(`/community/room/${room.id}`),
    accessScope: "members" as const,
  };

  return (
    <MemberPageScaffold
      title={room.name}
      fallbackPath="/communities?section=rooms"
      stickyHeader
      onTitlePress={() => setRoomActionsOpen(true)}
      titleActionLabel={`Open ${room.name} details and actions`}
      action={
        <AppIconButton
          label={`Open ${room.name} details and actions`}
          onPress={() => setRoomActionsOpen(true)}
        >
          <Info size={18} />
        </AppIconButton>
      }
    >
      <AppBottomSheetSurface
        open={roomActionsOpen}
        onOpenChange={(open) => {
          setRoomActionsOpen(open);
          if (!open) {
            setEditingRoom(false);
            setRoomNameDraft(room.name ?? "");
            setRoomDescriptionDraft(room.description ?? "");
          }
        }}
        title={room.name}
        description={`${memberCount} member${memberCount === 1 ? "" : "s"}`}
        maxWidthClassName="max-w-lg"
      >
        <div className="max-h-[calc(100dvh-10rem)] space-y-4 overflow-y-auto overscroll-contain pr-1">
          {canManageRoom && editingRoom ? (
            <div className="space-y-3">
              <AppFieldGroup label="Room name" helper={`${roomNameDraft.length}/50`}>
                <AppInput
                  value={roomNameDraft}
                  onChangeText={(value) => setRoomNameDraft(value.slice(0, 50))}
                  placeholder="Give your room a clear name"
                  maxLength={50}
                />
              </AppFieldGroup>
              <AppFieldGroup label="Description" helper={`${roomDescriptionDraft.length}/200`}>
                <AppTextArea
                  value={roomDescriptionDraft}
                  onChangeText={(value) => setRoomDescriptionDraft(value.slice(0, 200))}
                  placeholder="What should members use this room for?"
                  maxLength={200}
                  minHeight={120}
                />
              </AppFieldGroup>
              <AppButtonRow>
                <AppButton type="button" size="sm" onPress={() => void saveRoom()} disabled={!roomNameDraft.trim() || savingRoom}>
                  {savingRoom ? "Saving..." : "Save room"}
                </AppButton>
                <AppButton
                  type="button"
                  tone="outline"
                  size="sm"
                  onPress={() => {
                    setEditingRoom(false);
                    setRoomNameDraft(room.name ?? "");
                    setRoomDescriptionDraft(room.description ?? "");
                  }}
                >
                  Cancel
                </AppButton>
              </AppButtonRow>
            </div>
          ) : (
            <>
              <AppText color="$colorMuted" fontSize="$sm" lineHeight="$md">
                {room.description || "A focused space for one shared topic inside Community."}
              </AppText>
              <div className="space-y-2">
                {isMember ? (
                  <KindredShareSheet
                    payload={sharePayload}
                    triggerLabel="Share room"
                    triggerTone="outline"
                  />
                ) : null}
                <AppButton
                  type="button"
                  width="100%"
                  tone={isMember ? "outline" : "primary"}
                  onPress={() => void (isMember ? leaveRoom() : joinRoom())}
                >
                  {isMember ? "Leave room" : "Join room"}
                </AppButton>
                {canManageRoom ? (
                  <AppButton
                    type="button"
                    width="100%"
                    tone="outline"
                    onPress={() => setEditingRoom(true)}
                  >
                    Edit room
                  </AppButton>
                ) : null}
                {canManageRoom ? (
                  <AppButton
                    type="button"
                    width="100%"
                    tone="danger"
                    onPress={() => void deleteRoom()}
                    disabled={deletingRoom}
                  >
                    {deletingRoom ? "Deleting..." : "Delete room"}
                  </AppButton>
                ) : null}
              </div>
            </>
          )}
        </div>
      </AppBottomSheetSurface>

      {!isMember ? (
        <AppCard>
          <AppCardBody gap="$md">
            <AppText lineHeight="$md">
              Join this room to view the conversation, reply to other members, and share photos.
            </AppText>
            <AppButton type="button" onPress={() => void joinRoom()}>
              Join room
            </AppButton>
          </AppCardBody>
        </AppCard>
      ) : null}

      {isMember ? (
        <AppCard>
          <AppCardBody gap="$md">
            {messages.length === 0 ? (
              <AppEmptyState
                title="No messages yet"
                body="Say hello first and get the room started."
              />
            ) : (
              <div className="space-y-3">
                {messages.map((message: any) => {
                  const canEditMessage = canEditOwnedContentOnly({ viewerId: user?.id, ownerId: message.user_id });
                  const canRemoveMessage = canRemoveKinfeedOrChannelContent({
                    viewerId: user?.id,
                    ownerId: message.user_id,
                    role,
                  });
                  const isAdminModeratingMessage = isAdminModeratingOthersContent({
                    viewerId: user?.id,
                    ownerId: message.user_id,
                    role,
                  });
                  const repliedMessage = message.reply_to_id ? getReplyMessage(message.reply_to_id) : null;
                  const messageReactions = groupScopedReactions(reactions, "message_id", message.id, user?.id);
                  const selectedReaction = Object.entries(messageReactions).find(([, value]) => value.mine)?.[0];

                  return (
                    <div
                      key={message.id}
                      ref={(node) => {
                        if (node) {
                          messageRefs.current.set(message.id, node);
                        } else {
                          messageRefs.current.delete(message.id);
                        }
                      }}
                      className="space-y-2"
                    >
                      {message.id === firstUnreadMessageId ? (
                        <div className="flex items-center gap-3 py-1" aria-label="New messages start here">
                          <span className="h-px flex-1 bg-primary/30" />
                          <span className="text-xs font-semibold text-primary">New messages</span>
                          <span className="h-px flex-1 bg-primary/30" />
                        </div>
                      ) : null}
                      <div
                        className="flex gap-2"
                        {...longPressAction(() => setMessageActionsId(message.id))}
                      >
                        <Avatar className="h-8 w-8 shrink-0">
                          {message.profiles?.avatar_url ? <AvatarImage src={message.profiles.avatar_url} /> : null}
                          <AvatarFallback className="bg-primary/20 text-primary text-xs font-semibold">
                            {(message.profiles?.display_name ?? "K")[0]}
                          </AvatarFallback>
                        </Avatar>

                        <div className="min-w-0 flex-1 rounded-[22px] border border-border/70 bg-background/80 px-3 py-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="flex items-baseline gap-2">
                              <AppText fontSize="$sm" fontWeight="700">
                                {message.profiles?.display_name ?? "Member"}
                              </AppText>
                              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                                {formatTime(message.created_at)}
                              </AppText>
                            </div>
                          </div>

                          <div className="flex items-center gap-1">
                            <MessageActions
                              open={messageActionsId === message.id}
                              onOpenChange={(open) => setMessageActionsId(open ? message.id : null)}
                              selectedReaction={selectedReaction}
                              onReactionSelect={(emoji) => toggleReaction(message.id, emoji)}
                              actions={[
                                {
                                  id: "reply",
                                  label: "Reply",
                                  icon: <Reply size={16} />,
                                  onSelect: () => setReplyTo(message),
                                },
                                ...(!canRemoveMessage
                                  ? [
                                      {
                                        id: "report",
                                        label: "Report",
                                        icon: <Flag size={16} />,
                                        onSelect: () => {
                                          setReportMsgId(message.id);
                                          setReportOpen(true);
                                        },
                                      },
                                    ]
                                  : []),
                              ]}
                              onEdit={
                                canEditMessage
                                  ? () => {
                                      const parsed = parseKindredShareContent(message.content);
                                      setEditingId(message.id);
                                      setEditText(parsed.payload ? parsed.text : message.content);
                                    }
                                  : undefined
                              }
                              onDelete={canRemoveMessage ? () => void deleteMsg(message.id, isAdminModeratingMessage) : undefined}
                              deleteLabel={isAdminModeratingMessage ? "Hide message" : "Delete message"}
                              showEdit={canEditMessage}
                            />
                          </div>
                        </div>

                        {repliedMessage ? (
                          <div className="mt-2 rounded-lg border-l-2 border-primary bg-muted/60 px-2 py-1">
                            <AppText fontSize="$xs" fontWeight="700">
                              {repliedMessage.profiles?.display_name ?? "Member"}
                            </AppText>
                            <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                              {getKindredSharePreview(repliedMessage.content)}
                            </AppText>
                          </div>
                        ) : null}

                        {editingId === message.id ? (
                          <div className="mt-2 space-y-2">
                            <AppInput value={editText} onChangeText={setEditText} />
                            <div className="flex gap-2">
                              <AppButton type="button" size="sm" onPress={() => void saveEdit(message.id)}>
                                Save
                              </AppButton>
                              <AppButton type="button" tone="outline" size="sm" onPress={() => setEditingId(null)}>
                                Cancel
                              </AppButton>
                            </div>
                          </div>
                        ) : (
                          <div className="mt-2 space-y-2">
                            <KindredSharedContent content={message.content} textClassName="text-sm text-foreground" />
                            {message.edited_at ? (
                              <AppText color="$colorMuted" fontSize="$xs" lineHeight="$xs">
                                Edited
                              </AppText>
                            ) : null}
                            <ReactionBar
                              reactions={messageReactions}
                              showAddButton={false}
                              onToggleReaction={(emoji) => toggleReaction(message.id, emoji)}
                            />
                          </div>
                        )}

                        {message.photo_url && resolveStoredObjectUrl("channel-photos", message.photo_url, resolvedPhotoUrls) ? (
                          <button
                            type="button"
                            onClick={() => setFullScreenImage(resolveStoredObjectUrl("channel-photos", message.photo_url, resolvedPhotoUrls))}
                            className="mt-2 block max-w-[180px] overflow-hidden rounded-lg"
                          >
                            <img
                              src={resolveStoredObjectUrl("channel-photos", message.photo_url, resolvedPhotoUrls) ?? ""}
                              alt=""
                              className="w-full rounded-lg"
                            />
                          </button>
                        ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div ref={bottomRef} />
              </div>
            )}
          </AppCardBody>
        </AppCard>
      ) : null}

      {isMember ? (
        <AppCard>
          <AppCardBody gap="$md">
            <AppSectionHeader title="Reply" subtitle="Keep it useful and kind." />
            {replyTo ? (
              <ReplyQuote
                name={replyTo.profiles?.display_name ?? "Member"}
                preview={getKindredSharePreview(replyTo.content)}
                onCancel={() => setReplyTo(null)}
              />
            ) : null}
            <PhotoUploadButton
              bucket="channel-photos"
              onPhotoReady={setPhotoUrl}
              onClear={() => setPhotoUrl(null)}
              previewUrl={photoUrl}
              returnObjectPath
            />
            <div className="flex flex-col gap-3 sm:flex-row">
              <MentionComposerField
                value={newMsg}
                onChangeText={setNewMsg}
                mentions={newMsgMentions}
                onMentionsChange={setNewMsgMentions}
                lookupCandidates={(query) => searchChatRoomMentionSuggestions(roomId, query, user?.id)}
                placeholder="Type a message..."
                fieldProps={{ flex: 1 }}
                inputProps={{
                  flex: 1,
                  onKeyDown: (event: any) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void send();
                    }
                  },
                }}
              />
              <AppButton
                type="button"
                size="sm"
                onPress={() => void send()}
                disabled={sending || (!newMsg.trim() && !photoUrl)}
              >
                <Send size={16} />
                {sending ? "Sending..." : "Send"}
              </AppButton>
            </div>
          </AppCardBody>
        </AppCard>
      ) : null}

      {fullScreenImage ? (
        <FullScreenImage
          images={[fullScreenImage]}
          currentIndex={0}
          onClose={() => setFullScreenImage(null)}
          onNavigate={() => {}}
        />
      ) : null}

      <ReportSheet
        open={reportOpen}
        onOpenChange={setReportOpen}
        contentType="chat_message"
        contentId={reportMsgId ?? undefined}
      />
    </MemberPageScaffold>
  );
};

export default ChatRoomView;
